/** biome-ignore-all lint/complexity/noExcessiveCognitiveComplexity: router */
/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: router */

import { BulkTraderBreadcrumb as BulkTraderRevampBreadcrumb } from '@containers/BulkTraderDetail/bulkTraderBreadcrumb';
import { Layout } from '@containers/Layout';
import { FeatureFlagTypes, NavigationStyles, Pages } from '@generated/graphql';
import ErrorBoundary from '@ovComponents/3-pattern/errorBoundary/errorBoundary';
import { BulkRebalanceRouteGuard } from '@ovComponents/4-module/bulkRebalance';
import { AccountBreadcrumb } from '@pages/AccountDetail/accountBreadcrumb';
import { CustomReportBreadcrumb } from '@pages/AccountDetail/ReportDetail/customReportBreadcrumb';
import { AccountGroupBreadcrumb } from '@pages/AccountGroupDetail/accountGroupBreadcrumb';
import { FeeReportBreadcrumb } from '@pages/BillingCycleDetail/feeReportBreadcrumb';
import { PortfolioModelBreadcrumb } from '@pages/BuildModels/portfolioModelBreadcrumb';
import { BulkImportBreadcrumb } from '@pages/BulkImportDetail/bulkImportBreadcrumb';
import { ImportFileBreadcrumb } from '@pages/BulkImportFileDetail/importFileBreadcrumb';
import { BulkRebalanceBreadcrumb } from '@pages/BulkRebalanceDetail/bulkRebalanceBreadcrumb';
import { BulkUpsertItemsBreadcrumb } from '@pages/BulkUpsertRequestDetail/bulkUpsertItemsBreadcrumb';
import { ClientBreadcrumb } from '@pages/ClientDetail/clientBreadcrumb';
import { EntityBreadcrumb } from '@pages/ClientDetail/entityBreadcrumb';
import { ClientReportBreadcrumb } from '@pages/ClientReportDetail/clientReportBreadcrumb';
import { ConfigurableTablePageBreadcrumb } from '@pages/ConfigurableTableDetail/configurableTablePageBreadcrumb';
import { DailyFeeDetailsBreadcrumb } from '@pages/DailyFeeDetail/dailyFeeDetailsBreadcrumb';
import { DashboardBreadcrumb } from '@pages/DashboardDetail/dashboardBreadcrumb';
import { FormTemplateBreadcrumb } from '@pages/FormTemplateDetail/formTemplateBreadcrumb';
import { GoalBreadcrumb } from '@pages/GoalDetail/goalBreadcrumb';
import { ClientGroupBreadcrumb } from '@pages/HouseholdDetail/clientGroupBreadcrumb';
import { BulkTraderBreadcrumb } from '@pages/LegacyBulkTraderDetail/bulkTraderBreadcrumb';
import { OrgDetailsBreadcrumb } from '@pages/OrgDetail/orgDetailsBreadcrumb';
import { PortfolioOptimizerBreadcrumb } from '@pages/PortfolioOptimizerDetail/portfolioOptimizerBreadcrumb';
import { RebalanceBreadcrumb } from '@pages/RebalanceDetail/rebalanceBreadcrumb';
import { AccountSnapshotBreadcrumb } from '@pages/ReconciliationV2Detail/accountSnapshotBreadcrumb';
import { SubAccountBreadcrumb } from '@pages/SubAccountDetail/subAccountBreadcrumb';
import { AlertBannerBreadcrumb } from '@pages/setting/AlertBannerDetail/alertBannerBreadcrumb';
import { BillingScheduleBreadcrumb } from '@pages/setting/BillingScheduleDetail/billingScheduleBreadcrumb';
import { ClientReportTemplateBreadcrumb } from '@pages/setting/ClientReportTemplateDetail/clientReportTemplateBreadcrumb';
import { CustodianConnectionBreadcrumb } from '@pages/setting/CustodianConnectionDetail/custodianConnectionBreadcrumb';
import { FeeGridBreadcrumb } from '@pages/setting/FeeGridDetail/feeGridBreadcrumb';
import { HomePageConfigurationBreadcrumb } from '@pages/setting/HomePageConfigurationDetail/homePageConfigurationBreadcrumb';
import { PageConfigurationBreadcrumb } from '@pages/setting/PageConfigurationDetail/pageConfigurationBreadcrumb';
import { StaticPageWidgetConfigurationBreadcrumb } from '@pages/setting/StaticPageWidgetConfigurationDetail/staticPageWidgetConfigurationBreadcrumb';
import { WorkflowBreadcrumb } from '@pages/setting/WorkflowDetail/workflowBreadcrumb';
import { AllDocumentsBreadcrumb } from '@pages/staticDocumentList/allDocumentsBreadcrumb';
import { UserContext } from '@providers/userContextProvider';
import filterHouseholdsWithAccess from '@utils/filterHouseholdsWithAccess';
import { FlagsmithFlags } from '@utils/flagsmith';
import { BootSpinner } from 'hephaestus/web';
import { type ReactElement, useContext, useEffect, useMemo } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { useMenuMap } from './hooks/useMenuMap';
import { useOrganizationFeatureFlag } from './hooks/useOrganizationFeatureFlag';

// Pages load on demand via React Router's `lazy:`.

const lazyComponentShowcase = () => import('@pages/ComponentShowcase');
const lazyAccountGroups = () => import('@pages/AccountGroupList');
const lazyAccounts = () => import('@pages/AccountList');
const lazyAccountsReview = () => import('@pages/AccountReview');
const lazyAdvisorContactAdmin = () => import('@pages/AdvisorContact');
const lazyAllNewsAndInsights = () => import('@pages/AllNewsAndInsightList');
const lazyAllTransactions = () => import('@pages/AllTransactionList');
const lazyAnnualSuitabilityReview = () =>
  import('@pages/AnnualSuitabilityReview');
const lazyAssetClasses = () => import('@pages/AssetClassList');
const lazyBankAccountsToReview = () => import('@pages/BankAccountReview');
const lazyBillingManagement = () => import('@pages/BillingCycleList');
const lazyBulkUpsertItems = () => import('@pages/BulkUpsertRequestDetail');
const lazyBulkUpsertRequests = () => import('@pages/BulkUpsertRequestList');
const lazyClientReportTemplate = () =>
  import('@pages/setting/ClientReportTemplateDetail');
const lazyComplianceReview = () => import('@pages/ComplianceReview');
const lazyConfigurableTablePage = () =>
  import('@pages/ConfigurableTableDetail');
const lazyConfigureWorkflowPage = () => import('@pages/setting/WorkflowDetail');
const lazyDailyFeeDetails = () => import('@pages/DailyFeeDetail');
const lazyDashboard = () => import('@pages/DashboardDetail');
const lazyEditFormTemplate = () => import('@pages/FormTemplateDetail');
const lazyEditDashboard = () => import('@pages/setting/EditDashboard');
const lazyElderlyClients = () => import('@pages/ElderlyClientReview');
const lazyFeeReport = () => import('@pages/BillingCycleDetail');
const lazyHouseholds = () => import('@pages/HouseholdList');
const lazyIndividuals = () => import('@pages/IndividualList');
const lazyInstitutionsToReview = () => import('@pages/InstitutionReview');
const lazyManageNewsAndInsight = () =>
  import('@pages/ManageNewsAndInsightList');
const lazyModelFamilies = () => import('@pages/ModelFamilyList');
const lazyModelPortfolioDetails = () => import('@pages/ModelPortfolioDetail');
const lazyModelPortfolios = () => import('@pages/ModelPortfolioList');
const lazyNewReport = () => import('@pages/AccountDetail/CreateReport');
const lazyNonIndividuals = () => import('@pages/NonIndividualList');
const lazyPageConfiguration = () =>
  import('@pages/setting/PageConfigurationDetail');
const lazyPortfolioTemplates = () => import('@pages/PortfolioTemplateList');
const lazyProductShelfPage = () => import('@pages/ProductShelfList');
const lazyRebalanceReport = () => import('@pages/RebalanceReport');
const lazyReconciliation = () => import('@pages/Reconciliation');
const lazyAccountSnapshot = () => import('@pages/ReconciliationV2Detail');
const lazyReconciliationV2 = () => import('@pages/ReconciliationV2List');
const lazyReports = () => import('@pages/AccountDetail/ReportList');
const lazyStatementsReview = () => import('@pages/StatementReview');
const lazyStaticPageWidgetConfiguration = () =>
  import('@pages/setting/StaticPageWidgetConfigurationDetail');
const lazyStaticDocumentsPage = () => import('@pages/staticDocumentList');
const lazyTransactionMonitoring = () => import('@pages/TransactionMonitoring');
const lazyViewClientReport = () => import('@pages/ClientReportDetail');
const lazyViewPage = () => import('@pages/ViewDetail');
const lazyViewReport = () => import('@pages/AccountDetail/ReportDetail');
const lazyWorkflows = () => import('@pages/WorkflowList');
// --- @pages/settings ---
const lazyAccessSettings = () => import('@pages/setting/Access');
const lazyAccessUsersSettings = () => import('@pages/setting/AccessUserList');
const lazyAccountSettings = () => import('@pages/setting/Account');
const lazyAccountGroupSettings = () => import('@pages/setting/AccountGroup');
const lazyAccountsTypesSettings = () => import('@pages/setting/AccountType');
const lazyEditAlertBanner = () => import('@pages/setting/AlertBannerDetail');
const lazyNewAlertBanner = () => import('@pages/setting/CreateAlertBanner');
const lazyApiTokensSettings = () => import('@pages/setting/ApiTokenList');
const lazyAuthenticationSettings = () =>
  import('@pages/setting/Authentication');
const lazyAuthenticationDesignerSettings = () =>
  import('@pages/setting/AuthenticationDesigner');
const lazyBillingSchedulesSettings = () =>
  import('@pages/setting/BillingScheduleList');
const lazySalesTaxSettings = () => import('@pages/setting/SalesTax');
const lazyBillingSchedulePage = () =>
  import('@pages/setting/BillingScheduleDetail');
const lazyCreateBillingSchedulePage = () =>
  import('@pages/setting/CreateBillingSchedule');
const lazyBulkImportsSettings = () => import('@pages/setting/BulkImportList');
const lazyBulkImportFileLineItemsSettings = () =>
  import('@pages/setting/BulkImportFileDetail');
const lazyBulkImportFilesSettings = () =>
  import('@pages/setting/BulkImportDetail');
const lazyClientReports = () => import('@pages/setting/ClientReportList');
const lazyComplianceSettings = () => import('@pages/setting/Compliance');
const lazyConfigurableTablePageEditor = () =>
  import('@pages/setting/ConfigurableTableDetail');
const lazyConfigurableTablePagesSettings = () =>
  import('@pages/setting/ConfigurableTableList');
const lazyCustodianConnectionDetail = () =>
  import('@pages/setting/CustodianConnectionDetail');
const lazyCustodianConnectionsSettings = () =>
  import('@pages/setting/CustodianConnectionList');
const lazyCustomAttributeLabelsSettings = () =>
  import('@pages/setting/CustomAttributeLabelList');
const lazyCustomFieldsSettings = () => import('@pages/setting/CustomFieldList');
const lazyDashboards = () => import('@pages/setting/DashboardList');
const lazyDesignerSettings = () => import('@pages/setting/Designer');
const lazyDowntimeSchedulesSettings = () =>
  import('@pages/setting/DowntimeScheduleList');
const lazyEmbeddedSettings = () => import('@pages/setting/Embedded');
const lazyErrorPageDesignerSettings = () =>
  import('@pages/setting/ErrorPageDesigner');
const lazyFeatureFlagsSettings = () => import('@pages/setting/FeatureFlag');
const lazyFeeGrids = () => import('@pages/setting/FeeGridList/app');
const lazyFeeGridsSettings = () => import('@pages/setting/FeeGridList');
const lazyFeeGridPage = () => import('@pages/setting/FeeGridDetail');
const lazyCreateFeeGridPage = () => import('@pages/setting/CreateFeeGrid');
const lazyFeeSettings = () => import('@pages/setting/Fee');
const lazyFormDefinitionsSettings = () =>
  import('@pages/setting/FormDefinitionList');
const lazyForms = () => import('@pages/setting/FormList');
const lazyGeneralSettings = () => import('@pages/setting/General');
const lazyGeneralProductExperienceSettings = () =>
  import('@pages/setting/GeneralProductExperience');
const lazyHomePageConfigurationEditor = () =>
  import('@pages/setting/HomePageConfigurationDetail');
const lazyHomePageConfigurationsSettings = () =>
  import('@pages/setting/HomePageConfigurationList');
const lazyIntegrationSettings = () => import('@pages/setting/IntegrationList');
const lazyLocalizationSettings = () => import('@pages/setting/Localization');
const lazyLocalizationConfigurationSettings = () =>
  import('@pages/setting/LocalizationConfigurationList');
const lazyNewsAndInsights = () => import('@pages/setting/NewsAndInsightList');
const lazyNotifications = () => import('@pages/setting/NotificationList');
const lazyPageConfigurationsSettings = () =>
  import('@pages/setting/PageConfigurationList');
const lazyPortfolioManagementSettings = () =>
  import('@pages/setting/PortfolioManagement');
const lazyRepCodesSettings = () => import('@pages/setting/RepCodeList');
const lazyRoleProfilesSettings = () => import('@pages/setting/RoleProfileList');
const lazyRoleSettings = () => import('@pages/setting/Role');
const lazyRolesSettings = () => import('@pages/setting/RoleList');
const lazyStatementsAndTaxDocumentsSettings = () =>
  import('@pages/setting/StatementsAndTaxDocument');
const lazyStaticPageWidgetConfigurationsSettings = () =>
  import('@pages/setting/StaticPageWidgetConfigurationList');
const lazyTaxRankingsSettings = () => import('@pages/setting/TaxRanking');
const lazyTradesSettings = () => import('@pages/setting/Trade');
const lazyTradingSchedulesSettings = () =>
  import('@pages/setting/TradingScheduleList');
const lazyTransfersSettings = () => import('@pages/setting/Transfer');
const lazyWebhooksSettings = () => import('@pages/setting/WebhookList');
const lazyWorkflowsSettings = () => import('@pages/setting/WorkflowList');
const lazyAccountGroupsDetail = () => import('@pages/AccountGroupDetail');
const lazyAccountsDetail = () => import('@pages/AccountDetail');
const lazyBillingSchedules = () =>
  import('@pages/setting/BillingScheduleList/app');
const lazyBuildModels = () => import('@pages/BuildModels');
const lazyBulkImportFileLineItems = () => import('@pages/BulkImportFileDetail');
const lazyBulkImport = () => import('@pages/BulkImportDetail');
const lazyBulkImports = () => import('@pages/BulkImportList');
const lazyClientV2 = () => import('@pages/ClientDetail');
const lazyDevSettings = () => import('@pages/DevSettings');
const lazyGoalsDetail = () => import('@pages/GoalDetail');
const lazyHouseHold = () => import('@pages/HouseholdDetail');
const lazyBulkTrader = () => import('@pages/LegacyBulkTraderDetail');
const lazyBulkTraders = () => import('@pages/LegacyBulkTraderList');
const lazyOrgDetails = () => import('@pages/OrgDetail');
const lazyOrgSettings = () => import('@pages/OrgSettings');
const lazyPortfolioOptimizer = () => import('@pages/PortfolioOptimizerDetail');
const lazyPortfolioOptimizers = () => import('@pages/PortfolioOptimizerList');
const lazyRebalance = () => import('@pages/RebalanceDetail');
const lazyRebalances = () => import('@pages/RebalanceList');
const lazyRefinitivShowcase = () => import('@pages/RefinitivShowcase');
const lazySchedules = () => import('@pages/setting/TradingScheduleList/app');
const lazySubAccountDetail = () => import('@pages/SubAccountDetail');
const lazyFormMapper = () => import('@pages/FormMapper');
const lazyHomePage = () => import('@pages/HomePage');
const lazyPortfolioTradingHub = () => import('@pages/PortfolioTradingHub');
const lazyPortfolioTradingHubV2 = () => import('@pages/PortfolioTradingHubV2');
const lazyBulkRebalance = () => import('@pages/BulkRebalanceList');
const lazyBulkRebalanceDetail = () => import('@pages/BulkRebalanceDetail');
const lazyBulkTradersRevamp = () => import('@pages/BulkTraderList');
const lazyBulkTraderRevamp = () =>
  import('@ovComponents/4-module/bulkTrader/bulkTrader/bulkTrader').then(
    (mod) => ({ Component: mod.default }),
  );
const lazyReconActivities = () => import('@pages/ReconActivities');
const lazyReconFlags = () => import('@pages/ReconFlags');
const lazyFlagGlossary = () => import('@pages/FlagGlossaryList');
const lazyAlertBanners = () => import('@pages/setting/AlertBannerList');
const lazyTextDesignerSettings = () => import('@pages/setting/TextDesigner');
const lazyFlagPoliciesSettings = () => import('@pages/setting/FlagPolicy');
const lazyReconToleranceSettings = () =>
  import('@pages/setting/ReconTolerance');
const lazySiemDataFeedSettings = () =>
  import('@pages/setting/SiemDataFeedList');

const newsAndInsightsRoutes = (
  <Route>
    <Route
      path='newsAndInsights/:widgetId'
      lazy={lazyAllNewsAndInsights}
      handle={{ breadcrumb: 'navMenu:menu.newsAndInsights' }}
    />
  </Route>
);

const clientReportRoutes = (
  <Route>
    <Route
      path='clientReport/:clientReportTemplateId/:objectId'
      lazy={lazyViewClientReport}
      handle={{ breadcrumb: ClientReportBreadcrumb }}
    />
  </Route>
);

const subAccountRoutes = (
  <Route
    path='subaccount/:subAccountId'
    handle={{ breadcrumb: SubAccountBreadcrumb }}
  >
    <Route index lazy={lazySubAccountDetail} />
    {clientReportRoutes}
  </Route>
);

const accountRoutes = (
  <Route path='account/:accountId' handle={{ breadcrumb: AccountBreadcrumb }}>
    <Route index lazy={lazyAccountsDetail} />
    {clientReportRoutes}
    {subAccountRoutes}
  </Route>
);

const accountGroupRoutes = (
  <Route
    path='accountGroup/:accountGroupId'
    handle={{ breadcrumb: AccountGroupBreadcrumb }}
  >
    <Route index lazy={lazyAccountGroupsDetail} />
    {clientReportRoutes}
    {accountRoutes}
  </Route>
);

const goalAccountAccountGroupSubaccountRoutes = (
  <Route>
    <Route path='goal/:goalId' handle={{ breadcrumb: GoalBreadcrumb }}>
      <Route index lazy={lazyGoalsDetail} />
      {clientReportRoutes}
      {subAccountRoutes}
    </Route>

    {accountRoutes}

    {accountGroupRoutes}
  </Route>
);

const householdRoutes = (
  <Route>
    <Route
      path='household/:clientGroupId'
      handle={{ breadcrumb: ClientGroupBreadcrumb }}
    >
      <Route index lazy={lazyHouseHold} />
      {clientReportRoutes}
      {goalAccountAccountGroupSubaccountRoutes}
      {newsAndInsightsRoutes}
    </Route>
  </Route>
);

const accessibleRoutes = ({
  accessiblePages,
  isHouseholdDefaultView,
  featureFlags,
  fallbackRoute,
  hasHomePage,
}: {
  accessiblePages: Set<string>;
  isHouseholdDefaultView?: boolean;
  featureFlags: Set<FlagsmithFlags>;
  fallbackRoute: ReactElement | null;
  hasHomePage: boolean;
}) => (
  <Route path='/' element={<Layout />} errorElement={<ErrorBoundary />}>
    {hasHomePage ? (
      <Route index lazy={lazyHomePage} />
    ) : (
      <Route index element={fallbackRoute} />
    )}

    {accessiblePages.has(Pages.Home) && (
      <Route
        path='dashboard/:dashboardId'
        lazy={lazyDashboard}
        handle={{ breadcrumb: DashboardBreadcrumb }}
      />
    )}
    {accessiblePages.has(Pages.ProductShelf) && (
      <Route
        path='productShelf'
        lazy={lazyProductShelfPage}
        handle={{ breadcrumb: 'navMenu:menu.productShelfPage' }}
      />
    )}
    {accessiblePages.has(Pages.AssetClasses) && (
      <Route
        path='assetClasses'
        lazy={lazyAssetClasses}
        handle={{ breadcrumb: 'navMenu:menu.assetClassesPage' }}
      />
    )}
    {accessiblePages.has(Pages.Schedules) && (
      <Route
        path='schedules'
        lazy={lazySchedules}
        handle={{ breadcrumb: 'navMenu:menu.tradingSchedules' }}
      />
    )}
    {(accessiblePages.has(Pages.Households) || isHouseholdDefaultView) && (
      <Route
        path='households'
        handle={{ breadcrumb: 'navMenu:menu.households' }}
      >
        {accessiblePages.has(Pages.Households) && (
          <Route index lazy={lazyHouseholds} />
        )}
        <Route
          path=':clientGroupId'
          handle={{ breadcrumb: ClientGroupBreadcrumb }}
        >
          <Route index lazy={lazyHouseHold} />
          {clientReportRoutes}
          {accountGroupRoutes}
          {(accessiblePages.has(Pages.IndividualClients) ||
            accessiblePages.has(Pages.NonIndividualClients)) && (
            <Route>
              <Route
                path=':clients/:userId'
                handle={{ breadcrumb: ClientBreadcrumb }}
              >
                <Route index lazy={lazyClientV2} />
                {clientReportRoutes}
                {goalAccountAccountGroupSubaccountRoutes}
                {newsAndInsightsRoutes}
              </Route>
              {goalAccountAccountGroupSubaccountRoutes}
              {newsAndInsightsRoutes}
            </Route>
          )}
        </Route>
      </Route>
    )}
    {accessiblePages.has(Pages.Accounts) && (
      <Route
        path='accounts'
        handle={{ breadcrumb: 'navMenu:menu.accountsList' }}
      >
        <Route index lazy={lazyAccounts} />
      </Route>
    )}
    {accessiblePages.has(Pages.Accounts) &&
      !accessiblePages.has(Pages.IndividualClients) && (
        <Route
          path='clients/:userId/account/:accountId'
          handle={{ breadcrumb: AccountBreadcrumb }}
        >
          <Route index lazy={lazyAccountsDetail} />
          {clientReportRoutes}
        </Route>
      )}
    {accessiblePages.has(Pages.AccountGroups) && (
      <Route
        path='accountGroups'
        handle={{ breadcrumb: 'navMenu:menu.accountGroupsList' }}
      >
        <Route index lazy={lazyAccountGroups} />
        <Route
          path=':accountGroupId'
          handle={{ breadcrumb: AccountGroupBreadcrumb }}
        >
          <Route index lazy={lazyAccountGroupsDetail} />
          {clientReportRoutes}
          {accountRoutes}
        </Route>
      </Route>
    )}
    {accessiblePages.has(Pages.IndividualClients) && (
      <Route>
        <Route
          path='clients'
          handle={{ breadcrumb: 'navMenu:menu.individuals' }}
        >
          <Route index lazy={lazyIndividuals} />
          <Route path=':userId' handle={{ breadcrumb: ClientBreadcrumb }}>
            <Route index lazy={lazyClientV2} />
            {clientReportRoutes}
            {goalAccountAccountGroupSubaccountRoutes}
            {householdRoutes}
            {newsAndInsightsRoutes}
          </Route>
        </Route>
      </Route>
    )}
    {accessiblePages.has(Pages.NonIndividualClients) && (
      <Route>
        <Route
          path='nonIndividualClients'
          handle={{ breadcrumb: 'navMenu:menu.nonIndividualClients' }}
        >
          <Route index lazy={lazyNonIndividuals} />
          <Route path=':userId' handle={{ breadcrumb: EntityBreadcrumb }}>
            <Route index lazy={lazyClientV2} />
            {clientReportRoutes}
            {goalAccountAccountGroupSubaccountRoutes}
            {newsAndInsightsRoutes}
          </Route>
        </Route>
      </Route>
    )}
    {accessiblePages.has(Pages.BuildModels) && (
      <Route>
        <Route
          path='buildModels'
          handle={{ breadcrumb: 'navMenu:menu.modelPortfolios' }}
        >
          <Route index lazy={lazyBuildModels} />
          <Route
            path=':id'
            lazy={lazyBuildModels}
            handle={{ breadcrumb: PortfolioModelBreadcrumb }}
          />
        </Route>
      </Route>
    )}
    {accessiblePages.has(Pages.PortfolioModelFamilies) && (
      <Route>
        <Route
          path='modelFamilies'
          lazy={lazyModelFamilies}
          handle={{ breadcrumb: 'navMenu:menu.modelFamily' }}
        />
      </Route>
    )}
    {accessiblePages.has(Pages.ModelPortfolios) && (
      <Route>
        <Route
          path='modelPortfolios'
          handle={{ breadcrumb: 'navMenu:menu.modelPortfolios' }}
        >
          <Route index lazy={lazyModelPortfolios} />
          <Route
            path=':id'
            lazy={lazyModelPortfolioDetails}
            handle={{ breadcrumb: PortfolioModelBreadcrumb }}
          />
        </Route>
      </Route>
    )}
    {accessiblePages.has(Pages.PortfolioTemplates) && (
      <Route>
        <Route
          path='portfolioTemplates'
          lazy={lazyPortfolioTemplates}
          handle={{ breadcrumb: 'navMenu:menu.portfolioTemplates' }}
        />
      </Route>
    )}
    {accessiblePages.has(Pages.Rebalancing) && (
      <Route>
        <Route
          path='rebalances'
          handle={{ breadcrumb: 'navMenu:menu.rebalancing' }}
        >
          <Route index lazy={lazyRebalances} />
          <Route
            path=':id'
            lazy={lazyRebalance}
            handle={{ breadcrumb: RebalanceBreadcrumb }}
          />
        </Route>
      </Route>
    )}
    {accessiblePages.has(Pages.RebalanceReport) && (
      <Route
        path='rebalanceReport'
        lazy={lazyRebalanceReport}
        handle={{ breadcrumb: 'navMenu:menu.rebalanceReport' }}
      />
    )}
    {featureFlags.has(FlagsmithFlags.portfolio_trading_prototype) && (
      <Route
        path='portfolio-trading-prototype'
        lazy={lazyPortfolioTradingHub}
        handle={{ breadcrumb: 'portfolioTradingHub:title' }}
      />
    )}
    {featureFlags.has(FlagsmithFlags.portfolio_trading_prototype) && (
      <Route
        path='portfolio-trading-prototype-v2'
        lazy={lazyPortfolioTradingHubV2}
        handle={{ breadcrumb: 'portfolioTradingHub:titleV2' }}
      />
    )}
    {accessiblePages.has(Pages.PortfolioOptimizer) && (
      <Route
        path='portfolioOptimizers'
        handle={{ breadcrumb: 'navMenu:menu.portfolioOptimizer' }}
      >
        <Route index lazy={lazyPortfolioOptimizers} />
        <Route
          path=':id'
          lazy={lazyPortfolioOptimizer}
          handle={{ breadcrumb: PortfolioOptimizerBreadcrumb }}
        />
      </Route>
    )}
    {accessiblePages.has(Pages.PortfolioOptimizer) && (
      <Route
        path='rebalance'
        element={
          <BulkRebalanceRouteGuard>
            <Outlet />
          </BulkRebalanceRouteGuard>
        }
        handle={{ breadcrumb: 'navMenu:menu.bulkRebalance' }}
      >
        <Route index lazy={lazyBulkRebalance} />
        <Route
          path=':runId'
          lazy={lazyBulkRebalanceDetail}
          handle={{ breadcrumb: BulkRebalanceBreadcrumb }}
        />
      </Route>
    )}
    {accessiblePages.has(Pages.BulkTrader) && (
      <Route>
        <Route
          path='bulkTrader'
          handle={{ breadcrumb: 'navMenu:menu.bulkTrader' }}
        >
          <Route index lazy={lazyBulkTraders} />
          <Route
            path=':id'
            lazy={lazyBulkTrader}
            handle={{ breadcrumb: BulkTraderBreadcrumb }}
          />
        </Route>
        {featureFlags.has(FlagsmithFlags.bulk_trader_revamp) && (
          <Route
            path='bulkTraderRevamp'
            handle={{ breadcrumb: 'navMenu:menu.bulkTraderRevamp' }}
          >
            <Route index lazy={lazyBulkTradersRevamp} />
            <Route
              path=':id'
              lazy={lazyBulkTraderRevamp}
              handle={{ breadcrumb: BulkTraderRevampBreadcrumb }}
            />
          </Route>
        )}
      </Route>
    )}
    {accessiblePages.has(Pages.BulkImport) && (
      <Route>
        <Route
          path='bulkImport'
          handle={{ breadcrumb: 'navMenu:menu.bulkImport' }}
        >
          <Route index lazy={lazyBulkImports} />
          <Route
            path=':bulkImportId'
            handle={{ breadcrumb: BulkImportBreadcrumb }}
          >
            <Route index lazy={lazyBulkImport} />
            <Route
              path='file/:importFileId'
              lazy={lazyBulkImportFileLineItems}
              handle={{ breadcrumb: ImportFileBreadcrumb }}
            />
          </Route>
        </Route>
      </Route>
    )}
    {accessiblePages.has(Pages.ElderlyClients) && (
      <Route
        path='elderlyClients'
        lazy={lazyElderlyClients}
        handle={{ breadcrumb: 'navMenu:menu.elderlyClients' }}
      />
    )}
    {accessiblePages.has(Pages.SuspiciousTransactions) && (
      <Route
        path='transactionMonitoringTool'
        lazy={lazyTransactionMonitoring}
        handle={{ breadcrumb: 'navMenu:menu.transactionMonitoringTool' }}
      />
    )}
    {accessiblePages.has(Pages.BankAccountsToReview) && (
      <Route
        path='bankAccountsToReview'
        lazy={lazyBankAccountsToReview}
        handle={{ breadcrumb: 'navMenu:menu.bankAccountsToReview' }}
      />
    )}
    {accessiblePages.has(Pages.InstitutionsToReview) && (
      <Route
        path='institutionsToReview'
        lazy={lazyInstitutionsToReview}
        handle={{ breadcrumb: 'navMenu:menu.institutionsToReview' }}
      />
    )}
    {accessiblePages.has(Pages.AnnualSuitabilityReview) && (
      <Route
        path='annualSuitabilityReview'
        lazy={lazyAnnualSuitabilityReview}
        handle={{ breadcrumb: 'navMenu:menu.annualSuitabilityReview' }}
      />
    )}
    {accessiblePages.has(Pages.ComplianceReview) && (
      <Route
        path='complianceReview'
        lazy={lazyComplianceReview}
        handle={{ breadcrumb: 'navMenu:menu.complianceReview' }}
      />
    )}
    {accessiblePages.has(Pages.Reconciliation) && (
      <Route
        path='reconciliation'
        lazy={lazyReconciliation}
        handle={{ breadcrumb: 'navMenu:menu.reconciliation' }}
      />
    )}
    {accessiblePages.has(Pages.Reconciliation) &&
      featureFlags.has(FlagsmithFlags.recon_activities_dashboard) && (
        <Route
          path='reconciliationActivities'
          lazy={lazyReconActivities}
          handle={{ breadcrumb: 'navMenu:menu.activities' }}
        />
      )}
    {accessiblePages.has(Pages.Reconciliation) &&
      featureFlags.has(FlagsmithFlags.recon_flags_dashboard) && (
        <Route path='flags' handle={{ breadcrumb: 'navMenu:menu.flags' }}>
          <Route index lazy={lazyReconFlags} />
          <Route path='glossary' lazy={lazyFlagGlossary} />
        </Route>
      )}
    {accessiblePages.has(Pages.Reconciliation) && (
      <Route
        path='reconciliationV2'
        handle={{ breadcrumb: 'navMenu:menu.reconciliation' }}
      >
        <Route index lazy={lazyReconciliationV2} />
        <Route
          path=':id'
          lazy={lazyAccountSnapshot}
          handle={{ breadcrumb: AccountSnapshotBreadcrumb }}
        />
      </Route>
    )}
    {accessiblePages.has(Pages.StatementsReview) && (
      <Route
        path='statementsReview'
        lazy={lazyStatementsReview}
        handle={{ breadcrumb: 'navMenu:menu.statementsReview' }}
      />
    )}
    {accessiblePages.has(Pages.Transactions) && (
      <Route
        path='transactions'
        lazy={lazyAllTransactions}
        handle={{ breadcrumb: 'navMenu:menu.transactions' }}
      />
    )}
    {accessiblePages.has(Pages.AccountsReview) && (
      <Route
        path='accountsReview'
        lazy={lazyAccountsReview}
        handle={{ breadcrumb: 'navMenu:menu.accountsReview' }}
      />
    )}
    {accessiblePages.has(Pages.FeeGrids) && (
      <Route path='feeGrids' handle={{ breadcrumb: 'navMenu:menu.feeGrids' }}>
        <Route index lazy={lazyFeeGrids} />
        <Route path='new' lazy={lazyCreateFeeGridPage} />
        <Route
          path=':id'
          lazy={lazyFeeGridPage}
          handle={{ breadcrumb: FeeGridBreadcrumb }}
        />
      </Route>
    )}
    {accessiblePages.has(Pages.BillingSchedules) && (
      <Route
        path='billingSchedules'
        handle={{ breadcrumb: 'navMenu:menu.billingSchedules' }}
      >
        <Route index lazy={lazyBillingSchedules} />
        <Route path='new' lazy={lazyCreateBillingSchedulePage} />
        <Route
          path=':id'
          lazy={lazyBillingSchedulePage}
          handle={{ breadcrumb: BillingScheduleBreadcrumb }}
        />
      </Route>
    )}
    {accessiblePages.has(Pages.BillingManagement) && (
      <Route>
        <Route
          path='billingManagement'
          handle={{ breadcrumb: 'navMenu:menu.incurredFees' }}
        >
          <Route index lazy={lazyBillingManagement} />
          <Route
            path=':billingCycleId'
            handle={{ breadcrumb: FeeReportBreadcrumb }}
          >
            <Route index lazy={lazyFeeReport} />
            <Route
              path='subAccount/:subAccountId'
              lazy={lazyDailyFeeDetails}
              handle={{ breadcrumb: DailyFeeDetailsBreadcrumb }}
            />
          </Route>
        </Route>
      </Route>
    )}
    {accessiblePages.has(Pages.OrganizationSettings) && (
      <Route
        path='orgSettings'
        handle={{ breadcrumb: 'navMenu:menu.orgSettings' }}
      >
        <Route index lazy={lazyOrgSettings} />
        <Route
          path='configureWorkflow/:id'
          handle={{ breadcrumb: WorkflowBreadcrumb }}
          lazy={lazyConfigureWorkflowPage}
        />
        <Route
          path='pageConfiguration/:id'
          handle={{ breadcrumb: PageConfigurationBreadcrumb }}
          lazy={lazyPageConfiguration}
        />
        <Route
          path='staticPageWidgetConfiguration/:id'
          handle={{ breadcrumb: StaticPageWidgetConfigurationBreadcrumb }}
          lazy={lazyStaticPageWidgetConfiguration}
        />
        <Route
          path='clientReportTemplate/:id'
          handle={{ breadcrumb: ClientReportTemplateBreadcrumb }}
          lazy={lazyClientReportTemplate}
        />
        <Route
          path='orgDetails/:id'
          handle={{ breadcrumb: OrgDetailsBreadcrumb }}
          lazy={lazyOrgDetails}
        />
        {/* Kept for backward compatibility with orgSettings bookmarks/deep links. */}
        <Route
          path='editDashboard/:dashboardId'
          handle={{ breadcrumb: DashboardBreadcrumb }}
          lazy={lazyEditDashboard}
        />
        <Route
          path='formTemplate/:id'
          handle={{ breadcrumb: FormTemplateBreadcrumb }}
          lazy={lazyEditFormTemplate}
        />
      </Route>
    )}
    {accessiblePages.has(Pages.NewsAndInsight) && (
      <Route
        path='newsAndInsights'
        handle={{ breadcrumb: 'navMenu:menu.newsAndInsights' }}
      >
        <Route index lazy={lazyManageNewsAndInsight} />
      </Route>
    )}
    <Route>
      <Route
        path='configurableTablePage/:configurableTablePageId'
        handle={{ breadcrumb: ConfigurableTablePageBreadcrumb }}
      >
        <Route index lazy={lazyConfigurableTablePage} />
        <Route
          path='accounts/:accountId'
          handle={{ breadcrumb: AccountBreadcrumb }}
        >
          <Route index lazy={lazyAccountsDetail} />
          {featureFlags.has(FlagsmithFlags.mackenzie_release_3) && (
            <Route
              path='allDocuments'
              lazy={lazyStaticDocumentsPage}
              handle={{ breadcrumb: AllDocumentsBreadcrumb }}
            />
          )}
        </Route>
        <Route
          path='accountGroups/:accountGroupId'
          lazy={lazyAccountGroupsDetail}
          handle={{ breadcrumb: AccountGroupBreadcrumb }}
        />
        <Route
          path='goals/:goalId'
          lazy={lazyGoalsDetail}
          handle={{ breadcrumb: GoalBreadcrumb }}
        />
        <Route
          path='households/:clientGroupId'
          handle={{ breadcrumb: ClientGroupBreadcrumb }}
        >
          <Route index lazy={lazyHouseHold} />
          {accountGroupRoutes}
          <Route
            path='account/:accountId'
            lazy={lazyAccountsDetail}
            handle={{ breadcrumb: AccountBreadcrumb }}
          />
          <Route
            path='client/:userId'
            handle={{ breadcrumb: ClientBreadcrumb }}
          >
            <Route index lazy={lazyClientV2} />
            <Route
              path='account/:accountId'
              lazy={lazyAccountsDetail}
              handle={{ breadcrumb: AccountBreadcrumb }}
            />
          </Route>
        </Route>
        <Route path='clients/:userId' handle={{ breadcrumb: ClientBreadcrumb }}>
          <Route index lazy={lazyClientV2} />
          <Route
            path='account/:accountId'
            lazy={lazyAccountsDetail}
            handle={{ breadcrumb: AccountBreadcrumb }}
          />
        </Route>
        <Route
          path='nonIndividualClients/:userId'
          handle={{ breadcrumb: EntityBreadcrumb }}
        >
          <Route index lazy={lazyNonIndividuals} />
          <Route
            path='account/:accountId'
            lazy={lazyAccountsDetail}
            handle={{ breadcrumb: AccountBreadcrumb }}
          />
        </Route>
        <Route
          path='subAccounts/:subAccountId'
          lazy={lazySubAccountDetail}
          handle={{ breadcrumb: SubAccountBreadcrumb }}
        />
      </Route>
    </Route>
    {/* TODO: Temp path for testing */}
    <Route path='viewPage/:id/:objectId' lazy={lazyViewPage} />
    <Route
      path='viewClientReport/:clientReportTemplateId/:objectId'
      lazy={lazyViewClientReport}
    />

    {accessiblePages.has(Pages.DeveloperSettings) && (
      <Route>
        <Route
          path='devSettings'
          lazy={lazyDevSettings}
          handle={{ breadcrumb: 'navMenu:menu.devSettings' }}
        />
        <Route
          path='bulkUpsertRequests'
          handle={{ breadcrumb: 'navMenu:menu.bulkUpsertRequests' }}
        >
          <Route index lazy={lazyBulkUpsertRequests} />
          <Route
            path=':bulkUpsertRequestId'
            lazy={lazyBulkUpsertItems}
            handle={{ breadcrumb: BulkUpsertItemsBreadcrumb }}
          />
        </Route>
      </Route>
    )}
    {accessiblePages.has(Pages.Reports) && (
      <Route path='reports' handle={{ breadcrumb: 'navMenu:menu.reports' }}>
        <Route index lazy={lazyReports} />
        <Route
          path='new'
          lazy={lazyNewReport}
          handle={{ breadcrumb: 'customReports:newReport' }}
        />
        <Route
          path=':id'
          lazy={lazyViewReport}
          handle={{ breadcrumb: CustomReportBreadcrumb }}
        />
      </Route>
    )}
    {accessiblePages.has(Pages.Workflows) && (
      <Route
        path='workflows'
        lazy={lazyWorkflows}
        handle={{ breadcrumb: 'navMenu:menu.workflows' }}
      />
    )}

    <Route path='refinitivShowcase' lazy={lazyRefinitivShowcase} />

    {featureFlags.has(FlagsmithFlags.advisor_contact_repcode) && (
      <Route
        path='advisorContact'
        lazy={lazyAdvisorContactAdmin}
        handle={{ breadcrumb: 'navMenu:menu.advisorContact' }}
      />
    )}

    <Route path='settings'>
      <Route index element={<Navigate to='/settings/general' replace />} />
      <Route
        path='general'
        lazy={lazyGeneralSettings}
        handle={{ breadcrumb: 'navMenu:menu.general' }}
      />
      <Route
        path='designer'
        lazy={lazyDesignerSettings}
        handle={{ breadcrumb: 'navMenu:menu.designer' }}
      />
      {featureFlags.has(FlagsmithFlags.text_variants) && (
        <Route
          path='textDesigner'
          lazy={lazyTextDesignerSettings}
          handle={{ breadcrumb: 'navMenu:menu.textDesigner' }}
        />
      )}
      <Route
        path='authenticationTheme'
        lazy={lazyAuthenticationDesignerSettings}
        handle={{ breadcrumb: 'navMenu:menu.authenticationTheme' }}
      />
      <Route
        path='errorTheme'
        lazy={lazyErrorPageDesignerSettings}
        handle={{ breadcrumb: 'navMenu:menu.errorTheme' }}
      />
      <Route
        path='accessUsers'
        lazy={lazyAccessUsersSettings}
        handle={{ breadcrumb: 'navMenu:menu.accessUsers' }}
      />
      <Route
        path='authenticationSettings'
        lazy={lazyAuthenticationSettings}
        handle={{ breadcrumb: 'navMenu:menu.authentication' }}
      />
      <Route
        path='accessSettings'
        lazy={lazyAccessSettings}
        handle={{ breadcrumb: 'navMenu:menu.accessSettings' }}
      />
      <Route
        path='productExperience'
        lazy={lazyGeneralProductExperienceSettings}
        handle={{ breadcrumb: 'navMenu:menu.generalProductExperience' }}
      />
      <Route
        path='dashboards'
        handle={{ breadcrumb: 'navMenu:menu.dashboards' }}
      >
        <Route index lazy={lazyDashboards} />
        <Route
          path='editDashboard/:dashboardId'
          handle={{ breadcrumb: DashboardBreadcrumb }}
          lazy={lazyEditDashboard}
        />
      </Route>
      <Route
        path='localizations'
        lazy={lazyLocalizationSettings}
        handle={{ breadcrumb: 'navMenu:menu.localization' }}
      />
      <Route
        path='localizationConfiguration'
        lazy={lazyLocalizationConfigurationSettings}
        handle={{ breadcrumb: 'navMenu:menu.localizationConfiguration' }}
      />
      <Route
        path='notifications'
        lazy={lazyNotifications}
        handle={{ breadcrumb: 'navMenu:menu.notifications' }}
      />
      {featureFlags.has(FlagsmithFlags.alert_banners) && (
        <Route
          path='alertBanners'
          handle={{ breadcrumb: 'navMenu:menu.alertBanners' }}
        >
          <Route index lazy={lazyAlertBanners} />
          <Route
            path='new'
            lazy={lazyNewAlertBanner}
            handle={{ breadcrumb: 'alertBanners:newBanner' }}
          />
          <Route
            path=':id'
            lazy={lazyEditAlertBanner}
            handle={{ breadcrumb: AlertBannerBreadcrumb }}
          />
        </Route>
      )}
      <Route
        path='forms'
        lazy={lazyForms}
        handle={{ breadcrumb: 'navMenu:menu.forms' }}
      />
      <Route
        path='clientReports'
        handle={{ breadcrumb: 'navMenu:menu.clientReports' }}
      >
        <Route index lazy={lazyClientReports} />
        <Route
          path=':id'
          handle={{ breadcrumb: ClientReportTemplateBreadcrumb }}
          lazy={lazyClientReportTemplate}
        />
      </Route>
      <Route
        path='statementsAndTaxDocuments'
        lazy={lazyStatementsAndTaxDocumentsSettings}
        handle={{ breadcrumb: 'navMenu:menu.statementsAndTaxDocuments' }}
      />
      <Route
        path='customFields'
        lazy={lazyCustomFieldsSettings}
        handle={{ breadcrumb: 'navMenu:menu.customFields' }}
      />
      <Route
        path='repCodes'
        lazy={lazyRepCodesSettings}
        handle={{ breadcrumb: 'navMenu:menu.repCodes' }}
      />
      <Route
        path='customAttributeLabels'
        lazy={lazyCustomAttributeLabelsSettings}
        handle={{ breadcrumb: 'navMenu:menu.customAttributeLabels' }}
      />
      <Route
        path='pageConfigurations'
        handle={{ breadcrumb: 'navMenu:menu.pageConfigurations' }}
      >
        <Route index lazy={lazyPageConfigurationsSettings} />
        <Route
          path=':id'
          handle={{ breadcrumb: PageConfigurationBreadcrumb }}
          lazy={lazyPageConfiguration}
        />
      </Route>
      <Route
        path='homePageConfigurations'
        handle={{ breadcrumb: 'navMenu:menu.homePageConfigurations' }}
      >
        <Route index lazy={lazyHomePageConfigurationsSettings} />
        <Route
          path=':homePageConfigurationId'
          handle={{ breadcrumb: HomePageConfigurationBreadcrumb }}
          lazy={lazyHomePageConfigurationEditor}
        />
      </Route>
      <Route
        path='staticPageWidgetConfigurations'
        handle={{ breadcrumb: 'navMenu:menu.staticPageWidgetConfigurations' }}
      >
        <Route index lazy={lazyStaticPageWidgetConfigurationsSettings} />
        <Route
          path=':id'
          handle={{ breadcrumb: StaticPageWidgetConfigurationBreadcrumb }}
          lazy={lazyStaticPageWidgetConfiguration}
        />
      </Route>
      <Route
        path='configurableTablePages'
        handle={{ breadcrumb: 'navMenu:menu.configurableTablePages' }}
      >
        <Route index lazy={lazyConfigurableTablePagesSettings} />
        <Route
          path=':configurableTablePageId'
          handle={{ breadcrumb: ConfigurableTablePageBreadcrumb }}
          lazy={lazyConfigurableTablePageEditor}
        />
      </Route>
      <Route path='workflows' handle={{ breadcrumb: 'navMenu:menu.workflows' }}>
        <Route index lazy={lazyWorkflowsSettings} />
        <Route
          path=':id'
          handle={{ breadcrumb: WorkflowBreadcrumb }}
          lazy={lazyConfigureWorkflowPage}
        />
      </Route>
      <Route
        path='newsAndInsights'
        lazy={lazyNewsAndInsights}
        handle={{ breadcrumb: 'navMenu:menu.newsAndInsights' }}
      />
      <Route
        path='downtimeSchedule'
        lazy={lazyDowntimeSchedulesSettings}
        handle={{ breadcrumb: 'navMenu:menu.downtimeSchedule' }}
      />
      <Route
        path='transfers'
        lazy={lazyTransfersSettings}
        handle={{ breadcrumb: 'navMenu:menu.transfers' }}
      />
      <Route
        path='trades'
        lazy={lazyTradesSettings}
        handle={{ breadcrumb: 'navMenu:menu.trades' }}
      />
      <Route
        path='tradingSchedules'
        lazy={lazyTradingSchedulesSettings}
        handle={{ breadcrumb: 'navMenu:menu.tradingSchedules' }}
      />
      <Route
        path='assetClasses'
        lazy={lazyAssetClasses}
        handle={{ breadcrumb: 'navMenu:menu.assetClasses' }}
      />
      <Route
        path='compliance'
        lazy={lazyComplianceSettings}
        handle={{ breadcrumb: 'navMenu:menu.compliance' }}
      />
      {featureFlags.has(FlagsmithFlags.recon_flags_dashboard) && (
        <Route
          path='flagPolicies'
          lazy={lazyFlagPoliciesSettings}
          handle={{ breadcrumb: 'navMenu:menu.flagPolicies' }}
        />
      )}
      {featureFlags.has(FlagsmithFlags.recon_tolerance_config) && (
        <Route
          path='reconTolerance'
          lazy={lazyReconToleranceSettings}
          handle={{ breadcrumb: 'navMenu:menu.reconTolerance' }}
        />
      )}
      <Route
        path='portfolioManagementSettings'
        lazy={lazyPortfolioManagementSettings}
        handle={{ breadcrumb: 'navMenu:menu.portfolioManagementSettings' }}
      />
      <Route path='feeGrids' handle={{ breadcrumb: 'navMenu:menu.feeGrids' }}>
        <Route index lazy={lazyFeeGridsSettings} />
        <Route
          path='new'
          lazy={lazyCreateFeeGridPage}
          handle={{ breadcrumb: 'settings:feeGrids.createTitle' }}
        />
        <Route
          path=':id'
          lazy={lazyFeeGridPage}
          handle={{ breadcrumb: FeeGridBreadcrumb }}
        />
      </Route>
      <Route
        path='billingSchedules'
        handle={{ breadcrumb: 'navMenu:menu.billingSchedules' }}
      >
        <Route index lazy={lazyBillingSchedulesSettings} />
        <Route
          path='new'
          lazy={lazyCreateBillingSchedulePage}
          handle={{ breadcrumb: 'settings:billingSchedules.createTitle' }}
        />
        <Route
          path=':id'
          lazy={lazyBillingSchedulePage}
          handle={{ breadcrumb: BillingScheduleBreadcrumb }}
        />
      </Route>
      <Route
        path='salesTax'
        lazy={lazySalesTaxSettings}
        handle={{ breadcrumb: 'navMenu:menu.salesTax' }}
      />
      <Route
        path='feeSettings'
        lazy={lazyFeeSettings}
        handle={{ breadcrumb: 'navMenu:menu.feeSettings' }}
      />
      <Route
        path='apiTokens'
        lazy={lazyApiTokensSettings}
        handle={{ breadcrumb: 'navMenu:menu.apiTokens' }}
      />
      <Route
        path='webhooks'
        lazy={lazyWebhooksSettings}
        handle={{ breadcrumb: 'navMenu:menu.webhooks' }}
      />
      {featureFlags.has(FlagsmithFlags.siem_data_feed) && (
        <Route
          path='siemDataFeed'
          lazy={lazySiemDataFeedSettings}
          handle={{ breadcrumb: 'navMenu:menu.siemDataFeed' }}
        />
      )}
      <Route
        path='embedded'
        lazy={lazyEmbeddedSettings}
        handle={{ breadcrumb: 'navMenu:menu.embedded' }}
      />
      <Route
        path='roleSettings'
        lazy={lazyRoleSettings}
        handle={{ breadcrumb: 'navMenu:menu.roleSettings' }}
      />
      <Route
        path='roleProfiles'
        lazy={lazyRoleProfilesSettings}
        handle={{ breadcrumb: 'navMenu:menu.roleProfiles' }}
      />
      <Route
        path='roles'
        lazy={lazyRolesSettings}
        handle={{ breadcrumb: 'navMenu:menu.roles' }}
      />
      <Route
        path='accounts'
        lazy={lazyAccountsTypesSettings}
        handle={{ breadcrumb: 'navMenu:menu.accounts' }}
      />
      <Route
        path='accountSettings'
        lazy={lazyAccountSettings}
        handle={{ breadcrumb: 'navMenu:menu.accountSettings' }}
      />
      <Route
        path='accountGroupSettings'
        lazy={lazyAccountGroupSettings}
        handle={{ breadcrumb: 'navMenu:menu.accountGroupSettings' }}
      />
      <Route
        path='custodianConnections'
        handle={{ breadcrumb: 'navMenu:menu.custodianConnections' }}
      >
        <Route index lazy={lazyCustodianConnectionsSettings} />
        <Route
          path=':custodianConnectionId'
          lazy={lazyCustodianConnectionDetail}
          handle={{ breadcrumb: CustodianConnectionBreadcrumb }}
        />
      </Route>
      <Route
        path='taxRankings'
        lazy={lazyTaxRankingsSettings}
        handle={{ breadcrumb: 'navMenu:menu.taxRankings' }}
      />
      <Route
        path='formDefinitions'
        lazy={lazyFormDefinitionsSettings}
        handle={{ breadcrumb: 'navMenu:menu.formDefinitions' }}
      />
      <Route
        path='formMapper'
        lazy={lazyFormMapper}
        handle={{ breadcrumb: 'navMenu:menu.formMapper' }}
      />
      <Route
        path='featureFlags'
        lazy={lazyFeatureFlagsSettings}
        handle={{ breadcrumb: 'navMenu:menu.featureFlags' }}
      />
      <Route
        path='integrations'
        handle={{ breadcrumb: 'navMenu:menu.integrations' }}
      >
        <Route index lazy={lazyIntegrationSettings} />
        <Route
          path='bulkUpsertRequests'
          handle={{ breadcrumb: 'navMenu:menu.bulkUpsertRequests' }}
        >
          <Route index lazy={lazyBulkUpsertRequests} />
          <Route
            path=':bulkUpsertRequestId'
            lazy={lazyBulkUpsertItems}
            handle={{ breadcrumb: BulkUpsertItemsBreadcrumb }}
          />
        </Route>
      </Route>

      <Route>
        <Route
          path='bulkImport'
          handle={{ breadcrumb: 'navMenu:menu.bulkImport' }}
        >
          <Route index lazy={lazyBulkImportsSettings} />
          <Route
            path=':bulkImportId'
            handle={{ breadcrumb: BulkImportBreadcrumb }}
          >
            <Route index lazy={lazyBulkImportFilesSettings} />
            <Route
              path='file/:importFileId'
              lazy={lazyBulkImportFileLineItemsSettings}
              handle={{ breadcrumb: ImportFileBreadcrumb }}
            />
          </Route>
        </Route>
      </Route>
      <Route path='*' element={<Navigate to='/settings/general' replace />} />
    </Route>

    <Route path='components' lazy={lazyComponentShowcase} />

    <Route path='*' element={fallbackRoute} />
  </Route>
);

export const fallbackRedirectPath = (
  firstPath: string | undefined,
  hasHomePage: boolean,
  skipMenuRedirect = false,
) => {
  if (skipMenuRedirect && !hasHomePage) {
    return undefined;
  }
  if (hasHomePage) {
    return firstPath ?? '/';
  }
  if (!firstPath || firstPath === '/') {
    return undefined;
  }
  return firstPath;
};

export const Router = () => {
  const { userContext, activeEntity, accessiblePages, featureFlags } =
    useContext(UserContext);
  const { firstAccessibleMenuPath } = useMenuMap();
  const configurableHomePagesEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.ConfigurableHomePages,
  );
  const hasHomePage = !!(
    userContext.role?.homePageConfiguration && configurableHomePagesEnabled
  );

  const households = filterHouseholdsWithAccess(
    activeEntity?.households || [],
    activeEntity?.id,
    userContext,
  );
  const isHouseholdDefaultView =
    !!userContext.role?.makeHouseholdDefaultView && households.length > 0;

  // Simple + entity: entityMenu owns /clients/:id. A /clients hop flashes the table (OVT-5010).
  const skipMenuRedirect =
    userContext.role?.navigationStyle === NavigationStyles.Simple &&
    !!activeEntity?.id;
  const redirectTo = fallbackRedirectPath(
    firstAccessibleMenuPath,
    hasHomePage,
    skipMenuRedirect,
  );

  const router = useMemo(() => {
    const fallbackRoute = redirectTo ? (
      <Navigate to={redirectTo} replace />
    ) : null;
    const routesArray = createRoutesFromElements(
      accessibleRoutes({
        accessiblePages,
        isHouseholdDefaultView,
        featureFlags,
        fallbackRoute,
        hasHomePage,
      }),
    );
    return createBrowserRouter(routesArray);
  }, [
    accessiblePages,
    isHouseholdDefaultView,
    featureFlags,
    redirectTo,
    hasHomePage,
  ]);

  // Dispose the replaced/unmounted router so stale routers don't leak or navigate.
  useEffect(() => () => router.dispose(), [router]);

  return <RouterProvider router={router} fallbackElement={<BootSpinner />} />;
};
